import type { EstimateOption } from "../api/pricing";
import type {
  RecipientBookEntry,
  RecipientDestination,
} from "../api/recipients";
import type { IUserData } from "../shared/session/interfaces/session";
import { PayoutMethodType, type PayoutInput } from "../interfaces/payout";
import { payerCodesEquivalent, resolveCashPickupOption } from "./payerCode";

/**
 * Recipient-book destination replay helpers for flow-send.
 * Maps book destinations into `PayoutInput` and validates against live
 * pre-quote options (never re-pin `lastCapabilityKey` raw).
 */

/** Strips a country prefix from a book `stateCode` when present (`MX-JAL` → `JAL`). */
export const bareStateCode = (
  stateCode: string | undefined | null
): string | null => {
  if (!stateCode?.trim()) return null;
  const trimmed = stateCode.trim().toUpperCase();
  const dash = trimmed.indexOf("-");
  if (dash > 0 && dash <= 3) return trimmed.slice(dash + 1) || null;
  return trimmed;
};

export const destinationsForIntent = (
  destinations: RecipientDestination[],
  intent: PayoutMethodType | null
): RecipientDestination[] => {
  if (!intent) return [];
  return destinations.filter((d) => d.payoutMethod === intent);
};

const normalizeIdentityPart = (value: string | undefined | null): string =>
  (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const claimRecipientName = (
  userData: IUserData | undefined
): { firstName: string; lastName: string } | null => {
  if (userData?.name?.firstName && userData.name.lastName) {
    return userData.name;
  }
  const tokens = userData?.fullName?.trim().split(/\s+/).filter(Boolean) ?? [];
  if (tokens.length < 2) return null;
  return {
    firstName: tokens[0],
    lastName: tokens.slice(1).join(" "),
  };
};

const capabilityDestinationCountry = (
  capabilityKey: string | undefined
): string | null => {
  const country = capabilityKey?.split(":")[1]?.trim().toUpperCase();
  return country && /^[A-Z]{2}$/.test(country) ? country : null;
};

/**
 * Returns habits for the requester represented by the claim, scoped to the
 * claim's pinned destination country. Unknown-country legacy habits stay
 * hidden: replaying an MX instrument into AR is less safe than asking again.
 */
export const claimRecipientDestinations = (
  recipients: RecipientBookEntry[],
  userData: IUserData | undefined,
  destinationCountry: string
): RecipientDestination[] => {
  const claimName = claimRecipientName(userData);
  const country = destinationCountry.trim().toUpperCase();
  if (!claimName || !country) return [];

  const matchingEntries = recipients.filter((entry) => {
    const sameName =
      normalizeIdentityPart(entry.name.firstName) ===
        normalizeIdentityPart(claimName.firstName) &&
      normalizeIdentityPart(entry.name.lastName) ===
        normalizeIdentityPart(claimName.lastName);
    if (!sameName) return false;

    const entryCountry = entry.address?.countryISO2?.trim().toUpperCase();
    return !entryCountry || entryCountry === country;
  });

  return dedupeDestinations(
    matchingEntries.flatMap((entry) =>
      entry.destinations.filter(
        (destination) =>
          capabilityDestinationCountry(destination.lastCapabilityKey) === country
      )
    )
  );
};

/**
 * Maps a book destination into `PayoutInput` for the given intent.
 * Returns `null` when required fields for replay are missing.
 */
export const mapDestinationToPayoutInput = (
  destination: RecipientDestination,
  intent: PayoutMethodType
): PayoutInput | null => {
  if (destination.payoutMethod !== intent) return null;

  switch (intent) {
    case PayoutMethodType.CASH_PICKUP: {
      // Require state + payer. Payer-only habits (ANY SKU without
      // recipient.address.state) are not offered — the form always asks for
      // a subdivision, so one-tap must restore both.
      const stateCode = bareStateCode(destination.cash?.stateCode);
      const payerId = destination.cash?.payerCode?.trim() || null;
      if (!payerId || !stateCode) return null;
      return {
        source: "saved",
        payerId,
        stateCode,
      };
    }
    case PayoutMethodType.BANK_ACCOUNT: {
      if (!destination.paymentMethodId) return null;
      const last4 =
        destination.display?.maskedAccount?.replace(/\D/g, "").slice(-4) ||
        undefined;
      return {
        source: "saved",
        paymentMethodId: destination.paymentMethodId,
        bankId: destination.display?.bankName ?? undefined,
        last4,
      };
    }
    case PayoutMethodType.PUSH_TO_CARD: {
      if (!destination.paymentMethodId) return null;
      return {
        source: "saved",
        paymentMethodId: destination.paymentMethodId,
        brand: destination.display?.cardBrand ?? "unknown",
        last4: destination.display?.cardLast4 ?? "0000",
      };
    }
    case PayoutMethodType.MOBILE_WALLET: {
      const phone = destination.phone?.trim();
      if (!phone) return null;
      return { source: "saved", phone, carrierId: undefined };
    }
    default:
      return null;
  }
};

const payerCodeOf = (payout: PayoutInput, method: PayoutMethodType): string | null => {
  switch (method) {
    case PayoutMethodType.BANK_ACCOUNT:
      return payout.bankId ?? null;
    case PayoutMethodType.CASH_PICKUP:
      return payout.payerId ?? null;
    case PayoutMethodType.MOBILE_WALLET:
      return payout.carrierId ?? null;
    default:
      return null;
  }
};

/**
 * Resolves the live SKU for a payout selection from pre-quote options.
 */
export const resolveCapabilityKeyForPayout = (
  method: PayoutMethodType,
  payout: PayoutInput,
  options: EstimateOption[]
): string | null => {
  const forMethod = options.filter((o) => o.payoutMethod === method);
  if (forMethod.length === 0) return null;

  const payerCode = payerCodeOf(payout, method);

  if (method === PayoutMethodType.CASH_PICKUP) {
    if (!payerCode) return forMethod[0]?.capabilityKey ?? null;
    const forState = resolveCashPickupOption(
      options,
      payerCode,
      payout.stateCode
    );
    if (forState) return forState.capabilityKey;
    // The engine collapses state variants of a payer into one priced cell, so
    // a habit in another subdivision still replays while the payer is quotable.
    return (
      forMethod.find((o) => payerCodesEquivalent(o.payerCode, payerCode))
        ?.capabilityKey ?? null
    );
  }

  if (payerCode) {
    const byPayer = forMethod.find((o) =>
      payerCodesEquivalent(o.payerCode, payerCode)
    );
    if (byPayer) return byPayer.capabilityKey;
    if (method === PayoutMethodType.MOBILE_WALLET) {
      return null;
    }
  }

  if (
    (method === PayoutMethodType.BANK_ACCOUNT ||
      method === PayoutMethodType.PUSH_TO_CARD) &&
    (payout.source === "saved" || payout.paymentMethodId)
  ) {
    return forMethod[0]?.capabilityKey ?? null;
  }

  return forMethod[0]?.capabilityKey ?? null;
};

export interface CompatibleBookDestination {
  destination: RecipientDestination;
  payout: PayoutInput;
  capabilityKey: string;
}

export const compatibleBookDestinations = (
  destinations: RecipientDestination[],
  intent: PayoutMethodType | null,
  quoteOptions: EstimateOption[]
): CompatibleBookDestination[] => {
  if (!intent) return [];
  const out: CompatibleBookDestination[] = [];
  for (const destination of destinationsForIntent(destinations, intent)) {
    const payout = mapDestinationToPayoutInput(destination, intent);
    if (!payout) continue;
    const liveKey = resolveCapabilityKeyForPayout(
      intent,
      payout,
      quoteOptions
    );
    // While pre-quote options are empty (still loading), keep habits visible
    // via lastCapabilityKey. Once options resolve, require a live match so we
    // never one-tap a payer the engine can no longer quote.
    const capabilityKey =
      liveKey ??
      (quoteOptions.length === 0
        ? destination.lastCapabilityKey ?? null
        : null);
    if (!capabilityKey) continue;
    out.push({ destination, payout, capabilityKey });
  }
  return out;
};

/**
 * Collapses habits that render as the same row (same cash payer + state, same
 * instrument) across recipients, keeping the most recently used one. The book
 * is keyed by recipient, so a payer used for two people arrives twice and its
 * `destinationListKey` would collide as a React key.
 */
export const dedupeDestinations = (
  destinations: RecipientDestination[]
): RecipientDestination[] => {
  const byKey = new Map<string, RecipientDestination>();
  for (const destination of destinations) {
    const key = destinationListKey(destination);
    const current = byKey.get(key);
    if (!current || destination.lastUsedAt > current.lastUsedAt) {
      byKey.set(key, destination);
    }
  }
  return [...byKey.values()].sort((a, b) =>
    a.lastUsedAt < b.lastUsedAt ? 1 : a.lastUsedAt > b.lastUsedAt ? -1 : 0
  );
};

export const destinationListKey = (destination: RecipientDestination): string => {
  if (destination.paymentMethodId) return `pm:${destination.paymentMethodId}`;
  if (destination.cash) {
    return `cash:${destination.payoutMethod}:${destination.cash.stateCode ?? ""}:${destination.cash.payerCode ?? ""}`;
  }
  if (destination.phone) return `wallet:${destination.phone}`;
  return `method:${destination.payoutMethod}:${destination.lastUsedAt}`;
};

/**
 * When the recipient changes, drop a payout selection only if it pins a saved
 * instrument (`paymentMethodId`) that is not in the new recipient's book.
 * Cash pickup / wallet / newly typed PAN-CLABE have no pmId and stay armed.
 */
export const shouldClearPayoutOnRecipientChange = (
  payout: PayoutInput | null | undefined,
  destinations: RecipientDestination[]
): boolean => {
  const pmId = payout?.paymentMethodId;
  if (!pmId) return false;
  return !destinations.some((d) => d.paymentMethodId === pmId);
};
